import * as React from 'react'
import { MessageSquare } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { CoupleFeedbackDto, CoupleFeedbackNeedsAttentionDto, CoupleFeedbackWonderfulDto } from '@lib/dto/couple-feedback.dto'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useCoupleFeedbackQueue } from '@/hooks/useCoupleFeedback'
import { formatEventDate } from '@/lib/eventTime'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { EmptyState, ErrorState, ListSkeleton } from '@/components/ui/states'
import { guestCountLine, STATUS_LABEL, STATUS_TONE } from '@/lib/couple-feedback-labels'

function waitingLine(count: number): string {
  if (count === 0) return 'Nothing needs attention'
  if (count === 1) return '1 needs attention'
  return `${String(count)} need attention`
}

/** Resolved rows stay in the list. The count is the work still open. */
function openAttentionCount(rows: readonly CoupleFeedbackNeedsAttentionDto[]): number {
  return rows.filter((row) => row.status === 'new' || row.status === 'in_progress').length
}

function whenLine(row: CoupleFeedbackDto): string {
  const date = row.eventDate === null ? 'Date not set' : formatEventDate(row.eventDate)
  return `${row.eventName} · ${date} · ${guestCountLine(row.guestCount)} · ${row.venueName}`
}

function noteText(body: string | null): string {
  if (body !== null && body.length > 0) return body
  return 'No note.'
}

function AnswerCard({
  row,
  heading,
  footer,
}: {
  row: CoupleFeedbackDto
  heading: React.ReactNode
  footer?: React.ReactNode
}) {
  return (
    <Card asChild className="block min-w-0 wrap-anywhere p-4 transition-colors duration-100 hover:bg-surface-hover motion-reduce:transition-none">
      <Link to={`/feedback/${row.id}`}>
        {heading}
        <p className="m-0 min-w-0 wrap-anywhere text-small text-ink-muted">{whenLine(row)}</p>
        <p className="m-0 line-clamp-2 min-w-0 wrap-anywhere whitespace-pre-wrap text-sm">{noteText(row.body)}</p>
        {footer}
      </Link>
    </Card>
  )
}

/** Work: a status, and who has it. */
function AttentionCard({ row }: { row: CoupleFeedbackNeedsAttentionDto }) {
  const holder = row.assigneeName === null ? 'Unassigned' : `With ${row.assigneeName}`
  return (
    <AnswerCard
      row={row}
      heading={
        <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-2">
          <span className="min-w-0 wrap-anywhere font-medium text-ink">{row.authorName}</span>
          <Badge tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</Badge>
        </div>
      }
      footer={<p className="m-0 text-small text-ink-muted">{holder}</p>}
    />
  )
}

/** A record. No status and nobody assigned. */
function KindCard({ row }: { row: CoupleFeedbackWonderfulDto }) {
  return <AnswerCard row={row} heading={<span className="min-w-0 wrap-anywhere font-medium text-ink">{row.authorName}</span>} />
}

function Pile<Row extends { id: string }>({
  title,
  rows,
  empty,
  render,
}: {
  title: string
  rows: readonly Row[]
  empty: string
  render: (row: Row) => React.ReactNode
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-heading font-semibold text-ink">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-muted">{empty}</p>
      ) : (
        <ul className="grid gap-3">
          {rows.map((row) => (
            <li key={row.id} className="min-w-0">{render(row)}</li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * What couples said about the day. Problems are work. Kind words are kept,
 * and neither pile is a public review.
 */
export function FeedbackQueuePage() {
  const query = useCoupleFeedbackQueue()

  if (query.isPending) {
    return (
      <PageContainer width="wide" className="space-y-6">
        <PageHeader title="Feedback" />
        <ListSkeleton />
      </PageContainer>
    )
  }
  if (query.isError) {
    return (
      <PageContainer width="wide">
        <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
      </PageContainer>
    )
  }

  const list = query.data
  const none = list.needsAttention.length === 0 && list.kindWords.length === 0
  return (
    <PageContainer width="wide" className="space-y-8">
      <PageHeader
        title="Feedback"
        meta={waitingLine(openAttentionCount(list.needsAttention))}
        description="Private notes from couples about their experience. A public review on another site is separate, and isn’t matched to a person."
      />
      {none ? (
        <EmptyState
          icon={MessageSquare}
          title="No feedback yet"
          description="When a couple tells you how the day went, it lands here."
        />
      ) : (
        <>
          <Pile title="Needs attention" rows={list.needsAttention} empty="None right now." render={(row) => <AttentionCard row={row} />} />
          <Pile title="Kind words" rows={list.kindWords} empty="None yet." render={(row) => <KindCard row={row} />} />
        </>
      )}
    </PageContainer>
  )
}
